import React, { createContext, useContext, useEffect, useState } from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { removeSetUserSecurityPinBooleanToSessionStorage } from '../../shared/utils/pin-session';
import { useMyVwContext } from '../../shared/hooks/use-my-vw-context';
import { useFeatureServices } from '../../shared/hooks/use-feature-services';

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  sufficiencyChecksComplete: boolean;
  shouldTrackInitialLoad: boolean;

  login: () => Promise<void>;
  logout: () => Promise<void>;
  markInitialTrackingComplete: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Constants for session storage keys
const SESSION_STORAGE_KEYS = {
  INITIAL_TRACKING_COMPLETE: 'myvw_initial_tracking_complete',
  SESSION_ID: 'myvw_session_id',
} as const;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [sufficiencyChecksComplete, setSufficiencyChecksComplete] =
    useState<boolean>(false);
  const [shouldTrackInitialLoad, setShouldTrackInitialLoad] =
    useState<boolean>(false);

  const { authService } = useMyVwContext();
  const { myvwFaRolloutRegistration } = useFlags();
  const { 'locale-service': localeService } = useFeatureServices();
  const localeCode = `${localeService.language}-${localeService.countryCode}`;

  /**
   * Generates a unique session ID for tracking purposes
   */
  const generateSessionId = (): string => {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  /**
   * Checks if initial tracking should be performed for this session
   */
  const checkInitialTrackingStatus = (): boolean => {
    try {
      const currentSessionId = sessionStorage.getItem(
        SESSION_STORAGE_KEYS.SESSION_ID,
      );
      const trackingComplete = sessionStorage.getItem(
        SESSION_STORAGE_KEYS.INITIAL_TRACKING_COMPLETE,
      );

      // If no session ID exists, this is a new session
      if (!currentSessionId) {
        const newSessionId = generateSessionId();
        sessionStorage.setItem(SESSION_STORAGE_KEYS.SESSION_ID, newSessionId);
        sessionStorage.removeItem(
          SESSION_STORAGE_KEYS.INITIAL_TRACKING_COMPLETE,
        );
        return true;
      }

      // If session exists but tracking not complete, should track
      return trackingComplete !== 'true';
    } catch (error) {
      console.warn('Error checking initial tracking status:', error);
      return true; // Default to tracking if there's an error
    }
  };

  /**
   * Marks the initial tracking as complete for this session
   */
  const markInitialTrackingComplete = (): void => {
    try {
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.INITIAL_TRACKING_COMPLETE,
        'true',
      );
      setShouldTrackInitialLoad(false);
    } catch (error) {
      console.warn('Error marking initial tracking complete:', error);
    }
  };

  /**
   * Clears session tracking data on logout
   */
  const clearSessionTrackingData = (): void => {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.INITIAL_TRACKING_COMPLETE);
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.SESSION_ID);
    } catch (error) {
      console.warn('Error clearing session tracking data:', error);
    }
  };

  const runSufficiencyCheck = async () => {
    try {
      if (myvwFaRolloutRegistration === true) {
        await authService.sufficiencyCheckWithoutRedirection(localeCode);
      } else {
        await authService.sufficiencyCheck(localeCode);
      }

      setSufficiencyChecksComplete(true);

      await authService.getPartnerInfoForTracking(localeCode);
    } catch (error) {
      console.error(error);
      authService.apiLog({
        code: 'Error in running sufficiency checks',
        message: error,
      });
    }
  };

  const checkAuthentication = async () => {
    try {
      setLoading(true);

      const authStatus = await authService.isAuthenticated();
      setIsAuthenticated(authStatus);

      if (authStatus) {
        // Check if we should track initial load for this authenticated session
        const shouldTrack = checkInitialTrackingStatus();
        setShouldTrackInitialLoad(shouldTrack);

        await runSufficiencyCheck();
      } else {
        setSufficiencyChecksComplete(false);
        setShouldTrackInitialLoad(false);
        // Clear tracking data when not authenticated
        clearSessionTrackingData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only run when flag has a defined value (not undefined)
    if (myvwFaRolloutRegistration !== undefined) checkAuthentication();
  }, [authService, myvwFaRolloutRegistration]);

  const login = async () => {
    try {
      await authService.login();
      checkAuthentication();
    } catch (error) {
      console.error(error);
      authService.apiLog({
        code: 'Error in AccountManagementFeatureComponent: Login ',
        message: error,
      });
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      setShouldTrackInitialLoad(false);
      removeSetUserSecurityPinBooleanToSessionStorage();
      // Clear session tracking data on logout
      clearSessionTrackingData();
    } catch (error) {
      console.error(error);
      authService.apiLog({
        code: 'ERROR_CODES.ACCOUNT_SETTINGS handleLoginReset',
        message: error,
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        loading,
        login,
        logout,
        sufficiencyChecksComplete,
        shouldTrackInitialLoad,
        markInitialTrackingComplete,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) throw new Error('useAuth must be used within AuthProvider');

  return context;
};
